import { CreditScoreSuccess } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import { DISPUTE_LINK } from 'features/creditScore/constants/constants';
import StandardCardCarousel from 'features/rewards/components/rewardsCarousels/StandardCarousel';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CreditScoreCard } from 'shared/components/vxl/cards/CreditScoreCard/CreditScoreCard';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLeadingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import {
  showZendeskSection,
  ZendeskSections,
} from 'shared/utils/zendesk/zendeskUtils';

import { useCreditScoreData } from './useCreditScoreData';

interface ContentSectionProps {
  title: string;
  description: string;
}

const ContentSection = ({ title, description }: ContentSectionProps) => (
  <View style={{ padding: theme.spacing.m }}>
    <TitleMdText style={{ paddingBottom: theme.spacing.s }} numberOfLines={1}>
      {title}
    </TitleMdText>
    <BodySmText numberOfLines={0}>{description}</BodySmText>
  </View>
);

const AboutCreditScore = () => {
  const { t: translatedText } = useTranslation('creditScore', {
    keyPrefix: 'aboutCreditScorePage',
  });

  const { bottom } = useSafeAreaInsets();

  const {
    hasCreditScore,
    creditScore,
    fillToStep,
    factors,
    carouselOffers,
    sectionData,
    loading,
    error,
    refetch,
    goToLink,
  } = useCreditScoreData();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/credit/about-credit-score',
  });

  const faqsClickedEvent = usePressableAnalytics({
    analyticsId: 'faqsClicked',
    customAttributes: {
      faq_name: 'credit_score',
    },
  });
  const contactDisputeCenterClickedEvent = usePressableAnalytics({
    analyticsId: 'contactDisputeCenterClicked',
  });

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('navigationTitle'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView>
        <ScreenTransitionOverlay
          isLoading={loading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error ? (
              <DefaultPageDownErrorMessage
                error={error}
                message={error?.message ?? ''}
                tryAgainAction={refetch}
              />
            ) : null
          }
        />
        {hasCreditScore ? (
          <CreditScoreCard
            creditScore={creditScore?.score}
            creditScoreDelta={(creditScore as CreditScoreSuccess)?.scoreDelta}
            fillToStep={fillToStep}
            ranges={creditScore?.ranges}
            grade={creditScore?.grade}
            isDisabled={false}
            creditScoreDeltaTagline={
              (creditScore as CreditScoreSuccess)?.lastUpdated ??
              (creditScore as CreditScoreSuccess)?.description
            }
          />
        ) : null}

        <ContentSection
          title={translatedText('aboutTitle')}
          description={translatedText('aboutDescription')}
        />
        {hasCreditScore ? (
          <View testID="dataPointSection">
            {factors?.map((factor, index) => {
              const _leadingAccessory = {
                id: index,
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  variant: 'LG',
                  backgroundColor: theme.color.background.surface,
                  illustrationKey: factor.illustration,
                },
              } as ListItemLeadingAccessoryProps;

              const _trailingAccessory = {
                id: index,
                name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                props: {
                  primaryCopy: factor?.value,
                },
              } as ListItemTrailingAccessoryProps;

              return (
                <View key={factor?.name} testID="dataPoint">
                  <ListItem
                    showsChevron={false}
                    leadingAccessory={_leadingAccessory}
                    primaryCopy={factor?.name}
                    trailingAccessory={_trailingAccessory}
                    accessibilityLabel={`dataPoint-${index}`}
                  />

                  {index + 1 < factors.length ? (
                    <View style={styles.hairLineDivider}>
                      <HairLineDivider />
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : null}
        <View style={styles.sectionGap} />
        <ContentSection
          title={translatedText('understandTitle')}
          description={translatedText('understandDescription')}
        />
        <View style={styles.sectionGap} />
        {carouselOffers && sectionData ? (
          <View>
            <SectionDivider labelText={carouselOffers.node.name} />
            <StandardCardCarousel sectionData={sectionData} />
          </View>
        ) : null}
        <View style={styles.sectionGap} />
        <View>
          <SectionDivider labelText={translatedText('helpTitle')} />
          <ListItem
            key="CreditScoreFAQ"
            primaryCopy={translatedText('faqTitle')}
            showsChevron={false}
            onPress={() => {
              faqsClickedEvent();
              showZendeskSection(ZendeskSections.VARO_CREDIT_SCORE_FAQ);
            }}
          />
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
          <ListItem
            key="ContactDisputeCenter"
            primaryCopy={translatedText('disputeTitle')}
            showsChevron={false}
            onPress={() => {
              contactDisputeCenterClickedEvent();
              void goToLink(DISPUTE_LINK);
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.s,
  },
  sectionGap: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
});

export default AboutCreditScore;
